import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArrowLeft,
  Check,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import type {
  Contact,
  ContactAddress,
  ContactCustomField,
  ContactEmail,
  ContactPhone,
  ContactWithDetails,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { TokenInput } from "@/components/filters/TokenInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ContactForm } from "@/modules/contacts/screens/ContactForm";
import { RejectionNotice } from "@/modules/contacts/screens/RejectionNotice";
import { LinkedTasks } from "@/modules/tasks/components/LinkedTasks";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * One phone, email or address row, with its label and primary flag.
 *
 * `label` is `string | null` on all three children (see `contacts.ts`), so
 * "no label" and "no primary" are both ordinary, expected states here, not
 * missing data.
 */
function ChildRow({
  primary,
  label,
  children,
}: {
  primary: boolean;
  label: string | null;
  children: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span>{children}</span>
      {label ? <span className="text-muted-foreground">({label})</span> : null}
      {primary ? (
        <Badge variant="secondary" className="font-normal">
          Primary
        </Badge>
      ) : null}
    </li>
  );
}

function PhoneList({ phones, name }: { phones: ContactPhone[]; name: string }) {
  if (phones.length === 0) {
    // A blank card here would be indistinguishable from a card that failed
    // to render — two of the fixture's forty contacts genuinely have no
    // phone at all, and that has to read as a fact about the contact, not
    // as a bug in this screen.
    return (
      <p className="text-muted-foreground text-sm">No phone numbers on file.</p>
    );
  }

  return (
    <ul className="space-y-1">
      {phones.map((phone) => (
        <ChildRow key={phone.id} primary={phone.isPrimary} label={phone.label}>
          <PhoneNumber number={phone.number} name={name} />
        </ChildRow>
      ))}
    </ul>
  );
}

function EmailList({ emails }: { emails: ContactEmail[] }) {
  if (emails.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No email addresses on file.
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {emails.map((email) => (
        <ChildRow key={email.id} primary={email.isPrimary} label={email.label}>
          {email.email}
        </ChildRow>
      ))}
    </ul>
  );
}

/** The lines of a postal address, blank fields dropped rather than shown empty. */
function addressLines(address: ContactAddress["address"]): string {
  const cityRow = [address.city, address.province, address.zip]
    .filter((part) => part !== undefined && part.trim() !== "")
    .join(", ");

  return [address.line1, address.line2, cityRow, address.country]
    .filter((line) => line !== undefined && line.trim() !== "")
    .join("\n");
}

function AddressList({ addresses }: { addresses: ContactAddress[] }) {
  if (addresses.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No addresses on file.</p>
    );
  }

  return (
    <div className="space-y-3">
      {addresses.map((address) => {
        const lines = addressLines(address.address);
        return (
          <div key={address.id} className="rounded-md border p-3 text-sm">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-medium">{address.label ?? "Address"}</span>
              {address.isPrimary ? (
                <Badge variant="secondary" className="font-normal">
                  Primary
                </Badge>
              ) : null}
            </div>
            {lines === "" ? (
              <p className="text-muted-foreground">
                No address details on file.
              </p>
            ) : (
              <p className="whitespace-pre-line">{lines}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Tag editing for one contact (FR-CON-8).
 *
 * `tags` lives directly on `Contact`, so — unlike custom fields below — this
 * one has nowhere to hide: it goes straight through `contacts.update`, and a
 * rejection leaves `draft` untouched and `editing` open rather than closing
 * the form over a write that did not happen.
 */
function TagsEditor({ contact }: { contact: Contact }) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(contact.tags);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (tags: string[]) => data.contacts.update(contact.id, { tags }),
    onSuccess: (result) => {
      if (result.ok) {
        setEditing(false);
        setRejection(null);
        // Write-through, never write-local: redraw from whatever this
        // refetches rather than from `draft`, so the row shown is always
        // what the channel actually holds.
        void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (!editing) {
    return (
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm">Tags</CardTitle>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={() => {
              setDraft(contact.tags);
              setRejection(null);
              setEditing(true);
            }}
          >
            <Pencil />
          </Button>
        </CardHeader>
        <CardContent>
          {contact.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {contact.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="font-normal">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              No tags on this contact.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Tags</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <TokenInput
          values={draft}
          onChange={setDraft}
          placeholder="Type a tag and press Enter"
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate(draft);
            }}
          >
            <Check />
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={mutation.isPending}
            onClick={() => {
              setEditing(false);
              setRejection(null);
            }}
          >
            <X />
            Cancel
          </Button>
        </div>
        {rejection ? <RejectionNotice rejection={rejection} /> : null}
      </CardContent>
    </Card>
  );
}

/** One custom field row being edited. */
interface CustomFieldDraft {
  /**
   * A key for React and for targeting this row in local state only, never
   * sent. The interface takes the set as key/value pairs and reassigns ids,
   * because carrying an old row's id onto a renamed key would claim the two
   * are the same field when they are not.
   */
  draftId: string;
  key: string;
  value: string;
}

/**
 * Custom field editing for one contact (FR-CON-9).
 *
 * These are child rows on `ContactWithDetails`, not columns on `Contact`, so
 * they cannot go through `update`, which takes a `Partial<Contact>`. Rather
 * than force them past the type with a cast — which would mean writing
 * against one adapter's accidental leniency instead of against the interface
 * — `ContactsRepository` gained `setCustomFields`, and this calls it.
 *
 * The set is replaced wholesale because the form edits it as a set: add a
 * row, rename a key, delete one. Three endpoints to express one form is
 * three chances for the set to end up half-written.
 */
function CustomFieldsEditor({
  contactId,
  customFields,
}: {
  contactId: string;
  customFields: ContactCustomField[];
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (fields: CustomFieldDraft[]) =>
      data.contacts.setCustomFields(
        contactId,
        fields.map((field) => ({ key: field.key, value: field.value })),
      ),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        // Write-through: the rows come back from the adapter, so the card is
        // redrawn from what was stored rather than from the draft.
        void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const [draft, setDraft] = useState<CustomFieldDraft[]>(() =>
    customFields.map((field) => ({
      draftId: field.id,
      key: field.key,
      value: field.value,
    })),
  );
  // Counts up from the loaded rows so a freshly-added row's draftId never
  // collides with one already on the contact.
  const [nextId, setNextId] = useState(customFields.length);

  function updateField(
    draftId: string,
    patch: Partial<Pick<CustomFieldDraft, "key" | "value">>,
  ) {
    setDraft((prev) =>
      prev.map((field) =>
        field.draftId === draftId ? { ...field, ...patch } : field,
      ),
    );
  }

  function removeField(draftId: string) {
    setDraft((prev) => prev.filter((field) => field.draftId !== draftId));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Custom fields</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {draft.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No custom fields on this contact.
          </p>
        ) : (
          <ul className="space-y-2">
            {draft.map((field) => (
              <li key={field.draftId} className="flex items-center gap-2">
                <Input
                  value={field.key}
                  placeholder="Key"
                  onChange={(event) => {
                    updateField(field.draftId, { key: event.target.value });
                  }}
                  className="h-8 flex-1"
                />
                <Input
                  value={field.value}
                  placeholder="Value"
                  onChange={(event) => {
                    updateField(field.draftId, { value: event.target.value });
                  }}
                  className="h-8 flex-1"
                />
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Remove ${field.key || "field"}`}
                  onClick={() => {
                    removeField(field.draftId);
                  }}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setDraft((prev) => [
              ...prev,
              { draftId: `local-${nextId}`, key: "", value: "" },
            ]);
            setNextId((n) => n + 1);
          }}
        >
          <Plus />
          Add field
        </Button>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={
              mutation.isPending ||
              draft.some((field) => field.key.trim() === "")
            }
            onClick={() => {
              mutation.mutate(draft);
            }}
          >
            Save custom fields
          </Button>
          {draft.some((field) => field.key.trim() === "") ? (
            <span className="text-muted-foreground text-xs">
              Every field needs a key.
            </span>
          ) : null}
        </div>

        {rejection ? <RejectionNotice rejection={rejection} /> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Edit, archive and delete for the contact `ContactDetailBody` is showing
 * (#387, bullets 2–3).
 *
 * Lives beside the body rather than reached through `RecordDrawer`'s own
 * `actions` slot: that slot is filled by whoever renders the drawer, and
 * `ContactList` — which does — holds only the list row, not the phones,
 * emails and addresses a prefilled edit form needs. `ContactDetail` already
 * has the full `ContactWithDetails` from its own query, so the actions live
 * where the data already is.
 *
 * Archive writes straight through on click — it is reversible in the sense
 * that nothing here stops re-editing an archived contact, so it does not
 * carry the confirmation delete does. Delete does, because it is not
 * reversible at all.
 */
function ContactDetailActions({
  contact,
  onEdit,
  onDelete,
}: {
  contact: ContactWithDetails;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [archiveRejection, setArchiveRejection] =
    useState<WriteRejection | null>(null);

  const archiveMutation = useMutation({
    mutationFn: () => data.contacts.archive(contact.id),
    onSuccess: (result) => {
      if (result.ok) {
        setArchiveRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      } else {
        setArchiveRejection(result.rejection);
      }
    },
  });

  // Hiding is a courtesy, never the enforcement (FR-ACL-8) — the API calls
  // `requirePermission()` on every one of these routes regardless, and a
  // rejection from it renders through `RejectionNotice` the same as any
  // other write here.
  const canEdit = can(permissions, "contacts.contact.edit");
  const canDelete = can(permissions, "contacts.contact.delete");

  if (!canEdit && !canDelete) return null;

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        {canEdit ? (
          <Button type="button" size="sm" variant="outline" onClick={onEdit}>
            <Pencil />
            Edit
          </Button>
        ) : null}
        {canEdit && !contact.isArchived ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={archiveMutation.isPending}
            onClick={() => {
              setArchiveRejection(null);
              archiveMutation.mutate();
            }}
          >
            <Archive />
            Archive
          </Button>
        ) : null}
        {canDelete ? (
          <Button type="button" size="sm" variant="outline" onClick={onDelete}>
            <Trash2 />
            Delete
          </Button>
        ) : null}
      </div>
      {archiveRejection ? (
        <RejectionNotice rejection={archiveRejection} />
      ) : null}
    </div>
  );
}

/**
 * "Delete {name}?", naming the contact so a stray click cannot be mistaken
 * for confirming a different one — the same reasoning `RecordDrawer`'s title
 * follows for the panel it names.
 *
 * Closes the drawer itself on success via `useOpenRecord` rather than
 * through a prop from `ContactList`: the list only knows a record is open by
 * the URL's `record` param, and clearing it here is the same write the
 * drawer's own close button makes.
 */
function DeleteContactDialog({
  contact,
  open,
  onOpenChange,
}: {
  contact: ContactWithDetails;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const { close } = useOpenRecord();
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => data.contacts.delete(contact.id),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      close();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setRejection(null);
        onOpenChange(next);
      }}
    >
      <DialogContent title="Delete contact">
        <div className="space-y-3">
          <p className="text-sm">
            Delete <strong>{contact.displayName}</strong>? This removes their
            phones, emails, addresses, tags and custom fields, and cannot be
            undone.
          </p>
          {rejection ? <RejectionNotice rejection={rejection} /> : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={deleteMutation.isPending}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                setRejection(null);
                deleteMutation.mutate();
              }}
            >
              <Trash2 />
              Delete
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetailBody({ contact }: { contact: ContactWithDetails }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="max-w-2xl space-y-4">
      <header className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{contact.displayName}</h2>
            {contact.isArchived ? (
              <Badge variant="secondary">Archived</Badge>
            ) : null}
          </div>
          <ContactDetailActions
            contact={contact}
            onEdit={() => {
              setEditing(true);
            }}
            onDelete={() => {
              setConfirmingDelete(true);
            }}
          />
        </div>
        {contact.companyName ? (
          <p className="text-muted-foreground text-sm">{contact.companyName}</p>
        ) : null}
        {contact.types.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {/* A contact can hold more than one type at once (FR-CON-1) — a
                wholesale buyer who is also this business's karigar shows both
                badges, not whichever type happened to load first. */}
            {contact.types.map((type) => (
              <Badge key={type.id} variant="outline">
                {type.name}
              </Badge>
            ))}
          </div>
        ) : null}
      </header>

      {contact.description ? (
        <p className="text-sm">{contact.description}</p>
      ) : null}

      <TagsEditor contact={contact} />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">GSTIN</CardTitle>
        </CardHeader>
        <CardContent>
          {contact.gstin ? (
            <p className="text-sm tabular-nums">{contact.gstin}</p>
          ) : (
            <p className="text-muted-foreground text-sm">
              No GSTIN on file — an invoice to this contact is written as going
              to an unregistered buyer (FR-CON-10).
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Phone numbers</CardTitle>
        </CardHeader>
        <CardContent>
          <PhoneList phones={contact.phones} name={contact.displayName} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Email addresses</CardTitle>
        </CardHeader>
        <CardContent>
          <EmailList emails={contact.emails} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Addresses</CardTitle>
        </CardHeader>
        <CardContent>
          <AddressList addresses={contact.addresses} />
        </CardContent>
      </Card>

      <CustomFieldsEditor
        contactId={contact.id}
        customFields={contact.customFields}
      />

      <LinkedTasks targetType="contact" targetId={contact.id} />

      <ContactForm
        mode="edit"
        contact={contact}
        open={editing}
        onOpenChange={setEditing}
      />
      <DeleteContactDialog
        contact={contact}
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
      />
    </div>
  );
}

/**
 * One contact, with the children that live only on `ContactWithDetails` —
 * phones, emails, addresses and custom fields never travel with the list
 * row, so this fetches them itself rather than accepting them as a prop.
 *
 * There is no `/contacts/:id` route: routes are generated from
 * `config/modules.ts` (see `routes/router.tsx`), which only this
 * directory's caller may not edit, and a contact is not one of its
 * sections. `ContactList` renders this in place of the table instead, and
 * `onBack` is how it hands control back.
 */
export function ContactDetail({
  contactId,
  onBack,
}: {
  contactId: string;
  onBack?: () => void;
}) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["contacts", "detail", contactId],
    queryFn: () => data.contacts.get(contactId),
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Optional: the drawer this renders inside carries its own close, and two
        ways out of one panel side by side each look like they do something
        different.
      */}
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to list
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {query.isPending ? (
          <TableSkeleton rows={4} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data === null ? (
          <EmptyState
            title="Contact not found"
            description="It may have been deleted since this list was loaded."
          />
        ) : (
          <ContactDetailBody contact={query.data} />
        )}
      </div>
    </div>
  );
}
